import { useCallback, useEffect, useRef, useState } from "react";
import { CheckCircle2, ChevronLeft, ChevronRight, MessageSquareQuote, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { StarRating } from "./StarRating";

const ROTATE_MS = 6000;

export const TestimonialStrip = ({ className = "" }) => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const trackRef = useRef(null);
  const pausedRef = useRef(false);
  const canDelete = !!user?.is_super_admin;

  useEffect(() => {
    api.get("/testimonials").then(({ data }) => setItems(data)).catch(() => {});
  }, []);

  const page = useCallback((dir) => {
    const el = trackRef.current;
    if (!el) return;
    const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 8;
    if (dir > 0 && atEnd) el.scrollTo({ left: 0, behavior: "smooth" });
    else el.scrollBy({ left: dir * el.clientWidth, behavior: "smooth" });
  }, []);

  useEffect(() => {
    if (items.length < 2) return undefined;
    const t = setInterval(() => { if (!pausedRef.current) page(1); }, ROTATE_MS);
    return () => clearInterval(t);
  }, [items.length, page]);

  const remove = async (t) => {
    try {
      await api.delete(`/admin/testimonials/${t.id}`);
      setItems((xs) => xs.filter((x) => x.id !== t.id));
      toast.success("Testimonial removed");
    } catch {
      toast.error("Couldn't remove testimonial");
    }
  };

  if (items.length === 0) return null;
  return (
    <section
      className={`max-w-7xl mx-auto px-6 lg:px-8 ${className}`}
      data-testid="testimonial-strip"
      onMouseEnter={() => { pausedRef.current = true; }}
      onMouseLeave={() => { pausedRef.current = false; }}
      onTouchStart={() => { pausedRef.current = true; }}
    >
      <div className="flex items-end justify-between gap-4 mb-6">
        <div>
          <p className="text-[11px] tracking-[0.25em] uppercase font-semibold text-[#8C7B6B] flex items-center gap-2">
            <MessageSquareQuote className="w-3.5 h-3.5" /> Customer feedback
          </p>
          <h2 className="font-display text-base md:text-lg uppercase tracking-wide text-[#1A1A1A] mt-2">What customers say</h2>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => page(-1)} aria-label="Previous testimonials" data-testid="testimonial-prev" className="w-11 h-11 border border-[#DDD7CF] bg-white text-[#1A1A1A] hover:border-[#8C7B6B] hover:text-[#8C7B6B] transition-colors inline-flex items-center justify-center">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button type="button" onClick={() => page(1)} aria-label="Next testimonials" data-testid="testimonial-next" className="w-11 h-11 border border-[#DDD7CF] bg-white text-[#1A1A1A] hover:border-[#8C7B6B] hover:text-[#8C7B6B] transition-colors inline-flex items-center justify-center">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
      <div ref={trackRef} className="flex gap-4 overflow-x-auto snap-x snap-mandatory no-scrollbar -mx-6 px-6 scroll-pl-6 lg:mx-0 lg:px-0 lg:scroll-pl-0 pb-1" data-testid="testimonial-track">
        {items.map((t) => (
          <figure key={t.id} className="snap-start shrink-0 w-[85%] sm:w-[48%] lg:w-[32%] border border-[#DDD7CF] bg-white p-6 flex flex-col" data-testid={`testimonial-${t.id}`}>
            <div className="flex items-center justify-between gap-3">
              <StarRating value={t.rating} size="w-3.5 h-3.5" />
              {canDelete && (
                <button type="button" onClick={() => remove(t)} aria-label="Delete testimonial" data-testid={`delete-testimonial-${t.id}`} className="p-1 text-red-700 hover:text-red-900 transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
            <blockquote className="text-sm text-[#1A1A1A] leading-relaxed mt-4 flex-1">“{t.quote}”</blockquote>
            <figcaption className="mt-5 flex items-center gap-x-2 gap-y-1 flex-wrap text-xs text-[#6E675F]">
              <span className="font-semibold text-[#1A1A1A]" data-testid="testimonial-name">{t.name}</span>
              {t.location && <span>· {t.location}</span>}
              <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.1em] uppercase font-semibold text-[#2D6A4F] ml-auto">
                <CheckCircle2 className="w-3 h-3" /> Verified Customer
              </span>
            </figcaption>
          </figure>
        ))}
      </div>
    </section>
  );
};
